import { useState } from 'react';
import { brandInfo } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import PageMeta from '@/components/base/PageMeta';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 5000);
      return;
    }
    const formData = new FormData(form);
    try {
      const response = await fetch('https://readdy.ai/api/form/d9rgnfcnlsngrm4lmbug', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch (_err) { /* ignore parse error */ }
      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后再试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  const schemaJson = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://bluenav.cn' },
          { '@type': 'ListItem', position: 2, name: '关于我们' },
        ],
      },
      {
        '@type': 'Organization',
        name: '蓝色小导航',
        url: 'https://bluenav.cn',
        slogan: brandInfo.slogan,
        description: brandInfo.description,
        email: brandInfo.email,
        foundingDate: brandInfo.founded,
        address: { '@type': 'PostalAddress', addressCountry: 'CN' },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.8',
          reviewCount: '328',
        },
      },
    ],
  };

  return (
    <>
      <PageMeta
        title="关于我们"
        description="蓝色小导航 - 精致轻量的导航组件解决方案。了解我们的品牌理念、产品口碑与联系方式。"
        keywords="蓝色小导航,关于蓝色小导航,导航组件品牌,联系我们"
        canonicalUrl="https://bluenav.cn/about"
        schemaJson={schemaJson}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: '关于我们' },
          ]}
        />

        <section className="py-16 md:py-24 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center mb-16">
              <h1 className="text-3xl md:text-5xl font-bold text-foreground-950 mb-6">关于蓝色小导航</h1>
              <p className="text-sm md:text-base text-foreground-500 leading-relaxed">
                {brandInfo.description}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-20">
              {brandInfo.values.map((v, i) => (
                <div key={i} className="p-6 md:p-8 rounded-2xl bg-background-100/70 border border-background-200/60 text-center">
                  <div className="w-14 h-14 mx-auto mb-5 flex items-center justify-center rounded-2xl bg-primary-50 text-primary-600">
                    <i className={`text-2xl ${i === 0 ? 'ri-leaf-line' : i === 1 ? 'ri-flashlight-line' : 'ri-heart-line'}`}></i>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground-950 mb-3">{v.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{v.description}</p>
                </div>
              ))}
            </div>

            <div className="max-w-3xl mx-auto text-center mb-16">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4">用户口碑</h2>
              <p className="text-sm text-foreground-500 mb-10">听听开发者们怎么说</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {brandInfo.testimonials.map((t, i) => (
                  <div key={i} className="p-5 md:p-6 rounded-2xl bg-background-100/70 border border-background-200/60 text-left">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-11 h-11 rounded-full overflow-hidden flex-shrink-0 bg-secondary-100">
                        <img
                          src={t.avatarUrl}
                          alt={t.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-foreground-950">{t.name}</div>
                        <div className="text-xs text-foreground-500">{t.role}</div>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                  </div>
                ))}
              </div>
            </div>

            <div id="contact" className="max-w-2xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">联系我们</h2>
                <p className="text-sm text-foreground-500">有任何问题或建议？欢迎随时与我们取得联系</p>
              </div>

              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-5 bg-background-100/70 border border-background-200/60 rounded-2xl p-6 md:p-8"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-800 mb-1.5">姓名</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all duration-200"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-800 mb-1.5">邮箱</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all duration-200"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-foreground-800 mb-1.5">主题</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all duration-200"
                    placeholder="请简要描述您的问题"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-800 mb-1.5">留言内容</label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    required
                    maxLength={500}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all duration-200 resize-none"
                    placeholder="请输入您的留言内容（最多500字）"
                  ></textarea>
                </div>

                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="contact-hp"
                />

                <div className="flex flex-col sm:flex-row items-center gap-4">
                  <button
                    type="submit"
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-all duration-200 whitespace-nowrap"
                  >
                    <i className="ri-send-plane-line"></i>
                    发送消息
                  </button>

                  {formStatus === 'success' && (
                    <span className="text-sm text-accent-700 font-medium">消息已成功发送，我们会尽快回复您！</span>
                  )}
                  {formStatus === 'error' && (
                    <span className="text-sm text-red-600 font-medium">{formError || '提交失败，请稍后再试'}</span>
                  )}
                </div>
              </form>

              <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-4 rounded-xl bg-background-100/70 border border-background-200/60">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                    <i className="ri-mail-line"></i>
                  </div>
                  <div>
                    <div className="text-xs text-foreground-500">电子邮箱</div>
                    <div className="text-sm font-medium text-foreground-900">{brandInfo.email}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 rounded-xl bg-background-100/70 border border-background-200/60">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                    <i className="ri-map-pin-line"></i>
                  </div>
                  <div>
                    <div className="text-xs text-foreground-500">所在地</div>
                    <div className="text-sm font-medium text-foreground-900">{brandInfo.address}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}